/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { EuiCallOut } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useLocalStorage } from '@modules/utils/hooks/useLocalStorage'
import { CuiButton } from '@modules/cui/Button'
import { addToast } from '@modules/cui/Toasts'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import type { ProfileState } from '@modules/ui-types'
import { TrialIntentOptionsValueType } from '@modules/discovery-questions-lib/utils'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { isActiveTrialNotExtended } from '@modules/billing-lib/billingDetails/utils'

import { useEbtInterviewCallout } from './hooks'

import type { FC } from 'react'

export const messages = defineMessages({
  interested: {
    id: 'portal-resources.trial-interview.interested',
    defaultMessage: "I'm interested",
  },
  interestedLong: {
    id: 'portal-resources.trial-interview.interested-long',
    defaultMessage: "I'm interested! Accept to schedule a feedback call",
  },
  thankYou: {
    id: 'portal-resources.trial-interview.thank-you',
    defaultMessage: 'Thank you!',
  },
  expectEmail: {
    id: 'portal-resources.trial-interview.expect-email',
    defaultMessage: 'You can expect an email in the next 24 hours to schedule a time.',
  },
  trialCalloutTitle: {
    id: 'portal-resources.trial-interview-callout-title.default',
    defaultMessage: 'Free 2 week extension on your trial',
  },
  trialCalloutDescription: {
    id: 'portal-resources.trial-interview-callout-description.default',
    defaultMessage:
      "We're interested in your feedback on our serverless projects; if you're available for a 15-minute chat in the next week. After the short session, we'll extend your trial an additional 14 days.",
  },
})

const trialCalloutContent = {
  title: <FormattedMessage {...messages.trialCalloutTitle} />,
  description: <FormattedMessage {...messages.trialCalloutDescription} />,
}

export type InterviewCalloutProps = {
  profile: ProfileState
}

const InterviewCallout: FC<InterviewCalloutProps> = ({ profile }) => {
  const { formatMessage } = useIntl()
  const { reportTrialInterviewCalloutEbt } = useEbtInterviewCallout()
  const [isCalloutVisible, setCalloutVisible] = useLocalStorage(`interviewCalloutVisible`, true)
  const [, ldFlags] = useFlagsWhenLoaded()
  const billingDetails = useBillingDetails()

  if (!profile || !isCalloutVisible) {
    return null
  }

  const showTrialCallout =
    billingDetails.data &&
    isActiveTrialNotExtended(billingDetails.data) &&
    profile.data.discovery?.trial_intent !== TrialIntentOptionsValueType.learn &&
    ldFlags.showTrialInterviewCallout === true

  // For future customer callout extend this condition
  if (showTrialCallout) {
    const addThankYouToast = () => {
      addToast({
        title: formatMessage(messages.thankYou),
        color: 'success',
        text: formatMessage(messages.expectEmail),
      })
    }

    const handleInterestedClick = () => {
      // For future customer callout add check here to report different EBT event
      showTrialCallout && reportTrialInterviewCalloutEbt({ user_id: profile.user_id })
      addThankYouToast()
      setCalloutVisible(false)
    }

    const { title, description } = trialCalloutContent // For future customer callout add check here to display different content
    return (
      <EuiCallOut
        title={title}
        iconType='iInCircle'
        onDismiss={() => {
          setCalloutVisible(false)
        }}
        data-test-id='interview-callout'
      >
        <p>{description}</p>
        <CuiButton
          fill={true}
          aria-label={formatMessage(messages.interestedLong)}
          onClick={handleInterestedClick}
          data-test-id='interview-callout-interested-button'
        >
          <FormattedMessage {...messages.interested} />
        </CuiButton>
      </EuiCallOut>
    )
  }

  return null
}

export default InterviewCallout
